import {
  all,
  delay,
  put,
  race,
  retry,
  select,
  takeEvery,
} from 'redux-saga/effects';
import ActionTypes from '../../../../Data/ActionTypes';
import getCustomerMatchesQuery from './getCustomerMatchesQuery';
import MatchingSupplierCountSource from '../../../../Data/MatchingSupplierCountSource';
import {
  convertPartialSubmissionToInputAnswers,
  getMaxMatchesToDisplay,
  isMatchImpliedByPrivacyStatement,
  setSupplierMatchOptionsAndMarkSelected,
  shouldLegalStatementBeAboveNavigation,
  SUBCATEGORY_MAX_MATCHES_DEFAULT,
} from '../../../../Helpers/supplierMatches';
import { getPartialSubmission, getUrl } from '../../../../Helpers/submission';
import { exposeSupplierMatchesRequestedEvent } from '../../../../Helpers/events';
import { getCampaignIdTrumpedByUrl } from '../../../../Helpers/campaignId';
import { NAVIGATION_BLOCKS } from '../../../../Data/Constants';
import { incrementMetric } from '../../../../Services/Datadog';

export const MAX_TRIES = 3;
const QUERY_RETRY_DELAY_MS = 1000;
const MAX_REQUEST_DURATION_MS = 30000;

export const matchLevels = {
  SUBCATEGORY_REVENUE_FALLBACK: 'SUBCATEGORY_REVENUE_FALLBACK',
  LIVE: 'LIVE',
  SEVEN_DAY: 'SEVEN_DAY',
  PREDEFINED: 'PREDEFINED',
};

const REQUIRED_VARIABLES = [
  'subcategoryId',
  'countryCode',
  'visitorId',
  'answers',
];
const NO_CUSTOMER_MATCHES_ERROR =
  'Get customer matches request returned no matches';
export const GET_CUSTOMER_MATCHES_REQUEST_FAILED =
  'Get customer request failed';

const sendCustomerMatchMetric = (formConfig, matchType) => {
  incrementMetric({
    name: 'customerMatch',
    tags: {
      match_type: matchType,
      form_id: formConfig.formId,
      subcategory_id: formConfig.subcategoryId,
    },
  });
};

function* setAllCustomerMatchNavigationBlocks({
  isPreventingNavigation,
  pagesBlockedDuringCustomerMatchesRequest,
}) {
  yield all(
    pagesBlockedDuringCustomerMatchesRequest.map((pageNumber) =>
      put({
        type: 'NAVIGATION_CONTROLS/setNavigationBlockOnPage',
        payload: {
          pageNumber,
          blockName: NAVIGATION_BLOCKS.CUSTOMER_MATCHES,
          isPreventingNavigation,
        },
      })
    )
  );
}

/**
 * Retrieves supplier matches for a customer and updates supplier match component options accordingly.
 *
 * @generator
 * @function getCustomerMatchesWorker
 * @yields {object} - Redux action objects dispatched to update customer matches and set supplier matches.
 */
export function* getCustomerMatchesWorker() {
  const {
    components,
    supplierMatches,
    submission,
    formConfig,
    userIdentities,
    optionalFormConfig,
  } = yield select();
  const { iFrameId } = optionalFormConfig;
  const DEFAULT_HIDE_ON_SINGLE_MATCH = false;

  // Get all Supplier match components.
  const supplierMatchComponents = Object.values(components).filter(
    (component) => component.component.type === 'supplier-match'
  );

  const shouldGetMatches =
    (supplierMatchComponents.length > 0 &&
      supplierMatches.supplierMatchSelectionEnabled) ||
    supplierMatches?.showCustomerMatchesOnThankYouPage;

  if (!shouldGetMatches) {
    return;
  }

  const { pagesBlockedDuringCustomerMatchesRequest } = supplierMatches;
  let customerMatches;
  let customerMatchLevel;

  // Default in case there is no value on the component and the customer match request errors
  let subcategoryMaxMatches = SUBCATEGORY_MAX_MATCHES_DEFAULT;

  // Workaround: In the future max matches or SPL should be obtained on a per subcategory basis from the request. For now we use a value on the component
  const maxMatches =
    supplierMatchComponents[0]?.component?.maxMatches ||
    SUBCATEGORY_MAX_MATCHES_DEFAULT;

  const hideOnSingleMatch =
    supplierMatchComponents[0]?.component?.hideOnSingleMatch ||
    DEFAULT_HIDE_ON_SINGLE_MATCH;

  yield setAllCustomerMatchNavigationBlocks({
    isPreventingNavigation: true,
    pagesBlockedDuringCustomerMatchesRequest,
  });

  const campaignId =
    getCampaignIdTrumpedByUrl(
      formConfig.campaignId,
      getUrl(formConfig.pageUrl),
      optionalFormConfig.useCidFromURL
    ) ?? undefined;

  let hasDispatchedLoadingStart = false;
  try {
    yield put({
      type: 'SUPPLIER_MATCHES/requestMatchingSupplierCountLoading',
      payload: {
        isLoading: true,
        matchingSupplierCountLoadingSource: MatchingSupplierCountSource.MATCHES,
      },
    });
    hasDispatchedLoadingStart = true;

    // Get a list of supplier matches for the customer generated dynamically based on customer data.
    const answers = convertPartialSubmissionToInputAnswers(
      getPartialSubmission(components),
      supplierMatchComponents,
      supplierMatches.subcategoryMatches
    );

    const args = {
      variables: {
        subcategoryId: submission.webform.subcategoryId,
        countryCode: formConfig.countryCode,
        campaignId,
        visitorId: userIdentities.visitorId,
        answers,
        predefinedSupplierId: formConfig?.supplierId,
      },
    };

    const missingVariables = REQUIRED_VARIABLES.filter(
      (variable) => !args.variables[variable]
    );

    if (missingVariables.length > 0) {
      const missingVariablesString = missingVariables.join(', ');
      throw new Error(
        `Required variables (${missingVariablesString}) for the supplier match request are missing.`
      );
    }

    try {
      const { result, timeout } = yield race({
        result: retry(
          MAX_TRIES,
          QUERY_RETRY_DELAY_MS,
          getCustomerMatchesQuery,
          args
        ),
        timeout: delay(MAX_REQUEST_DURATION_MS), // 30 seconds timeout
      });

      if (timeout) {
        throw new Error('Request timed out after 30 seconds');
      }

      if (!timeout) {
        customerMatches = result.customerMatches;
        customerMatchLevel = result.matchLevel;
        subcategoryMaxMatches = result.maximumMatches;
      }
    } catch (error) {
      throw new Error(GET_CUSTOMER_MATCHES_REQUEST_FAILED);
    }

    if (customerMatches.length === 0) {
      throw new Error(NO_CUSTOMER_MATCHES_ERROR);
    }

    yield put({
      type: 'SUPPLIER_MATCHES/setCustomerMatches',
      payload: {
        customerMatches,
      },
    });

    yield put({
      type: 'SUPPLIER_MATCHES/setSubcategoryMaxMatches',
      payload: {
        subcategoryMaxMatches,
      },
    });

    const maxMatchesToDisplay = getMaxMatchesToDisplay({
      subcategoryMaxMatches,
      componentMaxMatches: maxMatches,
    });

    yield put({
      type: 'SUPPLIER_MATCHES/setRequiresLegalStatementAboveNav',
      payload: {
        requiresLegalStatementAboveNav: shouldLegalStatementBeAboveNavigation(
          customerMatches,
          maxMatchesToDisplay
        ),
      },
    });

    // It is unlikely we will need multiple supplier match components but in the event that this is necessary (conditionals etc) then loop through all supplier match components setting their options.
    yield all(
      supplierMatchComponents.map((supplierMatchComponent) =>
        setSupplierMatchOptionsAndMarkSelected(
          supplierMatchComponent,
          customerMatches,
          subcategoryMaxMatches
        )
      )
    );

    const liveMatchesCount = Math.min(
      customerMatches.length,
      subcategoryMaxMatches
    );

    yield put({
      type: 'SUPPLIER_MATCHES/setMatchingSupplierCount',
      payload: {
        matchingSupplierCount: liveMatchesCount,
        matchingSupplierCountSource: MatchingSupplierCountSource.MATCHES,
      },
    });
  } catch (error) {
    // Fallback matches have been provided from Capture in the form of the highest revenue brands for this subcategory. Set these as component options and mark as selected.
    yield all(
      supplierMatchComponents.map((supplierMatchComponent) =>
        setSupplierMatchOptionsAndMarkSelected(
          supplierMatchComponent,
          supplierMatches.subcategoryMatches,
          subcategoryMaxMatches
        )
      )
    );

    yield put({
      type: 'SUPPLIER_MATCHES/setCustomerMatches',
      payload: {
        customerMatches: [],
      },
    });

    yield put({
      type: 'SUPPLIER_MATCHES/setSubcategoryMaxMatches',
      payload: {
        subcategoryMaxMatches,
      },
    });

    const fallbackMatchesCount = Math.min(
      supplierMatches.subcategoryMatches.length,
      subcategoryMaxMatches
    );

    yield put({
      type: 'SUPPLIER_MATCHES/setMatchingSupplierCount',
      payload: {
        matchingSupplierCount: fallbackMatchesCount,
        matchingSupplierCountSource: MatchingSupplierCountSource.FALLBACK,
      },
    });

    const maxMatchesToDisplay = getMaxMatchesToDisplay({
      subcategoryMaxMatches,
      componentMaxMatches: maxMatches,
    });

    yield put({
      type: 'SUPPLIER_MATCHES/setRequiresLegalStatementAboveNav',
      payload: {
        requiresLegalStatementAboveNav: shouldLegalStatementBeAboveNavigation(
          supplierMatches.subcategoryMatches,
          maxMatchesToDisplay
        ),
      },
    });

    yield setAllCustomerMatchNavigationBlocks({
      isPreventingNavigation: false,
      pagesBlockedDuringCustomerMatchesRequest,
    });

    exposeSupplierMatchesRequestedEvent({
      eventName: 'customerMatchesRequested',
      iFrameId,
      isSuccess: false,
      maxMatches: maxMatchesToDisplay.toString(),
      isMatchImpliedByPrivacyStatement: isMatchImpliedByPrivacyStatement(
        hideOnSingleMatch,
        maxMatchesToDisplay.toString(),
        supplierMatches.subcategoryMatches,
        supplierMatches.supplierMatchSelectionEnabled
      ),
    });

    sendCustomerMatchMetric(
      formConfig,
      matchLevels.SUBCATEGORY_REVENUE_FALLBACK
    );

    yield put({
      type: 'SUPPLIER_MATCHES/setMatchLevel',
      payload: {
        matchLevel: matchLevels.SUBCATEGORY_REVENUE_FALLBACK,
      },
    });

    // In the event that customer specific supplier matches cannot be obtained form the query, fail silently and fallback to subcategory matches which have already been set.
    if (
      error.message === GET_CUSTOMER_MATCHES_REQUEST_FAILED ||
      error.message === NO_CUSTOMER_MATCHES_ERROR
    ) {
      return;
    }

    throw error;
  } finally {
    if (hasDispatchedLoadingStart) {
      yield put({
        type: 'SUPPLIER_MATCHES/requestMatchingSupplierCountLoading',
        payload: {
          isLoading: false,
          matchingSupplierCountLoadingSource: null,
        },
      });
    }
  }
  yield setAllCustomerMatchNavigationBlocks({
    isPreventingNavigation: false,
    pagesBlockedDuringCustomerMatchesRequest,
  });

  const maxMatchesToDisplay = getMaxMatchesToDisplay({
    subcategoryMaxMatches,
    componentMaxMatches: maxMatches,
  });

  exposeSupplierMatchesRequestedEvent({
    eventName: 'customerMatchesRequested',
    iFrameId,
    isSuccess: true,
    maxMatches: maxMatchesToDisplay.toString(),
    supplierMatches: customerMatches,
    customerMatchLevel,
    isMatchImpliedByPrivacyStatement: isMatchImpliedByPrivacyStatement(
      hideOnSingleMatch,
      maxMatchesToDisplay.toString(),
      customerMatches,
      supplierMatches.supplierMatchSelectionEnabled
    ),
  });

  sendCustomerMatchMetric(formConfig, customerMatchLevel);

  yield put({
    type: 'SUPPLIER_MATCHES/setMatchLevel',
    payload: {
      matchLevel: customerMatchLevel,
    },
  });
}

export default function* getCustomerMatchesWatcher() {
  yield takeEvery(ActionTypes.GET_CUSTOMER_MATCHES, getCustomerMatchesWorker);
}
